import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAuth } from 'react-oidc-context';
import { Outlet, useLocation } from 'react-router-dom';
/**
 * LoadingSpinner — shown while the OIDC session is being restored.
 */
function LoadingSpinner() {
    return (_jsx("div", { role: "status", "aria-label": "Loading...", className: "flex min-h-screen items-center justify-center", children: _jsx("div", { className: "h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" }) }));
}
/**
 * ForbiddenPage — shown when a user lacks the required role for a route.
 * Renders a non-technical message with no PHI or internal route details.
 */
export function ForbiddenPage() {
    return (_jsx("div", { role: "main", className: "flex min-h-screen items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("h1", { className: "text-2xl font-semibold text-gray-900", children: "Access denied" }), _jsxs("p", { className: "mt-2 text-gray-600", children: ["You don't have permission to view this page.", _jsx("br", {}), "Contact", ' ', _jsx("a", { href: "mailto:support@nourishedrx.com", className: "text-blue-600 underline", children: "NourishedRx support" }), ' ', "if you believe this is an error."] })] }) }));
}
/**
 * RequireAuth — React Router v7 layout route guard.
 *
 * Behaviour:
 * - isLoading → renders LoadingSpinner
 * - !isAuthenticated → calls signinRedirect with returnTo state, renders null
 * - requiredRolePrefix set → user must have at least one group starting with prefix; else ForbiddenPage
 * - requiredRole set (and no requiredRolePrefix) → user must have exact role; else ForbiddenPage
 * - neither set → any authenticated user passes
 * - all checks pass → renders Outlet
 */
export function RequireAuth({ requiredRole, requiredRolePrefix, }) {
    const auth = useAuth();
    const location = useLocation();
    if (auth.isLoading) {
        return _jsx(LoadingSpinner, {});
    }
    if (!auth.isAuthenticated) {
        void auth.signinRedirect({
            state: { returnTo: location.pathname + location.search },
        });
        return null;
    }
    const groups = auth.user?.profile['cognito:groups'];
    const roles = Array.isArray(groups)
        ? groups.filter((g) => typeof g === 'string')
        : [];
    if (requiredRolePrefix) {
        if (!roles.some((r) => r.startsWith(requiredRolePrefix))) {
            return _jsx(ForbiddenPage, {});
        }
    }
    else if (requiredRole) {
        if (!roles.includes(requiredRole)) {
            return _jsx(ForbiddenPage, {});
        }
    }
    return _jsx(Outlet, {});
}
//# sourceMappingURL=auth-guard.js.map